import { Directive, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { DateTime } from 'luxon';

@Directive({
    selector: '[libDateFormatter]'
})
export class DateFormatterDirective {
    format: string;
    @Output() keyboardDateBlur: EventEmitter<boolean>;
    @Output() keyboardDateChange: EventEmitter<string>;
    minDate: DateTime;

    constructor(private readonly elementRef: ElementRef) {
        this.format = 'MM/dd/yyyy';
        this.keyboardDateBlur = new EventEmitter<boolean>();
        this.keyboardDateChange = new EventEmitter<string>();
        this.minDate = DateTime.fromFormat('12/31/1969', 'MM/dd/yyyy');
    }

    private parse(date: string, isDelete: boolean, inputValue: string | null): string {
        const separator: '-' | '/' = this.format.indexOf('-') > 0 ? '-' : '/';

        const regex = new RegExp(`[^0-9${separator}]`, 'g');
        const sanitizedDate = date.replace(regex, '');

        const month = this.parseMonth(sanitizedDate, inputValue, isDelete, separator);
        const day = this.parseDay(sanitizedDate, inputValue, isDelete, separator);
        const year = this.parseYear(sanitizedDate, inputValue, isDelete, separator);

        date = month + day + year;
        const parsedDate = date.slice(0, this.format.length);

        return parsedDate;
    }

    private getSeparatorsPosition(separator: string): [number, number] {
        const dateSections = this.format.split(separator);
        const positionSeparator1 = dateSections[0].length;
        const positionSeparator2 = dateSections[0].length + dateSections[1].length + 1;
        return [positionSeparator1, positionSeparator2];
    }

    /**
     * add separators in correct positions in case that only digits are added
     */
    private autoInsertDateSeparators(date: string, separator: string, pos1: number, pos2: number): string {
        if (date.charAt(pos1) && date.charAt(pos1) !== separator) {
            date = date.slice(0, pos1) + separator + date.slice(pos1);
        }

        if (date.charAt(pos2) && date.charAt(pos2) !== separator) {
            date = date.slice(0, pos2) + separator + date.slice(pos2);
        }
        return date;
    }

    /**
     * remove separators from invalid positions from sanitizedDate
     */
    private removeInvalidDateSeparators(sanitizedDate: string, separator: string, pos1: number, pos2: number): string {
        return sanitizedDate
            .split('')
            .filter((char, i) => {
                return char !== separator || (char === separator && (i === pos1 || i === pos2)) ? char : '';
            })
            .join('');
    }

    private isValidStringDate(date: string): boolean {
        const formattedDate: DateTime = DateTime.fromFormat(date, this.format);
        return formattedDate.isValid && formattedDate > this.minDate;
    }

    private matchesFormat(date: string): boolean {
        const formattedDate = DateTime.fromFormat(date, this.format);
        return formattedDate.toFormat(this.format) === date;
    }

    private parseMonth(date: string, inputValue: string | null, isDelete: boolean, separator: '-' | '/'): string {
        const startIndex = this.format.indexOf('M');
        const endIndex = this.format.lastIndexOf('M');
        let monthWithSeparator = date.slice(startIndex, endIndex + 2);
        const month = date.slice(startIndex, endIndex + 1).replaceAll(separator, '');
        const monthInt = parseInt(month);
        if (inputValue === separator) {
            if (isNaN(monthInt)) {
                return '';
            } else if (monthInt === 0) {
                return month[0];
            } else if (month.length === 1) {
                monthWithSeparator = '0' + month + separator;
            } else if (month.length === 2) {
                monthWithSeparator = month + separator;
            }
        } else if (!isDelete) {
            if (monthInt > 12 || monthInt === 0) {
                return month[0];
            } else if (month.length === 1 && monthInt > 1 && monthInt < 10) {
                monthWithSeparator = '0' + month + separator;
            } else if (month.length === 2) {
                monthWithSeparator = month + separator;
            }
        }

        return monthWithSeparator;
    }

    private parseDay(date: string, inputValue: string | null, isDelete: boolean, separator: '-' | '/'): string {
        const startIndex = this.format.indexOf('d');
        const endIndex = this.format.lastIndexOf('d');
        let dayWithSeparator = date.slice(startIndex, endIndex + 2);
        const day = date.slice(startIndex, endIndex + 1).replaceAll(separator, '');
        const dayInt = parseInt(day);
        if (inputValue === separator) {
            if (isNaN(dayInt)) {
                return '';
            } else if (dayInt === 0) {
                return '0';
            } else if (day.length === 1) {
                dayWithSeparator = '0' + day + separator;
            } else if (day.length === 2) {
                dayWithSeparator = day + separator;
            }
        } else if (!isDelete) {
            if (dayInt > 31 || dayInt < 1) {
                dayWithSeparator = day[0];
            } else if (day.length === 1 && dayInt > 3 && dayInt < 10) {
                dayWithSeparator = '0' + day + separator;
            } else if (day.length === 2) {
                dayWithSeparator = day + separator;
            }
        }

        return dayWithSeparator;
    }

    private parseYear(date: string, inputValue: string | null, isDelete: boolean, separator: '-' | '/'): string {
        const startIndex = this.format.indexOf('y');
        const endIndex = this.format.lastIndexOf('y');
        const year = date.slice(startIndex, endIndex + 1).replaceAll(separator, '');
        if (inputValue === separator) {
            return year;
        }
        return !isDelete && year.length === 4 && year < '1969' ? '1969' : year;
    }

    private checkIfAddSeparator(date: string, pos1: number, pos2: number): string {
        if (date.length === pos1 || date.length === pos2) {
            return date + '/';
        }
        return date;
    }

    @HostListener('input', ['$event'])
    onInput(event: Event) {
        const inputEvent = event as InputEvent;
        const isDelete = inputEvent.inputType === 'deleteContentBackward';
        const inputValue = this.elementRef.nativeElement.value;
        this.elementRef.nativeElement.value = this.parse(inputValue, isDelete, inputEvent.data);
        this.keyboardDateChange.emit(this.parse(inputValue, isDelete, inputEvent.data));
    }

    @HostListener('blur')
    onBlur() {
        const inputValue = this.elementRef.nativeElement.value;
        if (this.isValidStringDate(inputValue) && this.matchesFormat(inputValue)) {
            this.keyboardDateBlur.emit(true);
        } else {
            this.keyboardDateBlur.emit(false);
        }
    }
}
